import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    Box, Flex, Text, Button, Switch, VStack, HStack,
    Spinner, Center, Tag, Tooltip, Icon, IconButton,
    AlertDialog, AlertDialogOverlay, AlertDialogContent,
    AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react';
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LuPencil, LuGripVertical, LuPlus, LuTrash2 } from 'react-icons/lu';
import { getPlatforms, updatePlatform, reorderPlatforms, createPlatform, deletePlatform } from './SdkConfigApi';
import AddPlatformModal from './AddPlatformModal';
import { getDocType } from './sdkDocsModel';

const SortableRow = ({ platform, onToggle, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: platform.id });

    return (
        <Flex
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
            align="center"
            p={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            gap={4}
            boxShadow={isDragging ? 'md' : 'none'}
        >
            <Box
                {...attributes}
                {...listeners}
                cursor="grab"
                color="gray.400"
                _hover={{ color: 'gray.600' }}
                flexShrink={0}
            >
                <LuGripVertical size={18} />
            </Box>

            <Box
                w="10px" h="10px" borderRadius="full"
                bg={platform.tag_color || 'gray.400'}
                flexShrink={0}
            />

            <Box flex={1}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    {platform.label}
                </Text>
                <Text fontSize="xs" color="gray.400">
                    v{platform.version}
                    {platform.variants?.length > 0 && (
                        <Box as="span" ml={2} color="blue.400">
                            {platform.variants.map(v => v.label).join(' / ')}
                        </Box>
                    )}
                </Text>
            </Box>

            <Tag colorScheme={getDocType(platform.type_id).colorScheme} variant="subtle" fontSize="10px">
                {getDocType(platform.type_id).label}
            </Tag>

            <Tag colorScheme={platform.enabled ? 'green' : 'gray'} fontSize="10px">
                {platform.enabled ? 'Enabled' : 'Disabled'}
            </Tag>

            <Tooltip label={platform.enabled ? 'Disable' : 'Enable'}>
                <Box display="inline-block">
                    <Switch
                        isChecked={platform.enabled}
                        onChange={() => onToggle(platform)}
                        colorScheme="blue"
                        size="sm"
                    />
                </Box>
            </Tooltip>

            <Tooltip label="Edit documentation">
                <Button
                    size="sm"
                    leftIcon={<Icon as={LuPencil} />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => onEdit(platform.id)}
                >
                    Edit
                </Button>
            </Tooltip>

            <Tooltip label="Delete platform">
                <IconButton
                    size="sm"
                    icon={<Icon as={LuTrash2} />}
                    colorScheme="red"
                    variant="outline"
                    aria-label="Delete platform"
                    onClick={() => onDelete(platform)}
                />
            </Tooltip>
        </Flex>
    );
};

const SdkConfigPage = () => {
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [adding, setAdding] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const navigate = useNavigate();
    const sensors = useSensors(useSensor(PointerSensor));
    const cancelDeleteRef = useRef(null);

    const loadPlatforms = async () => {
        setLoading(true);
        try {
            const data = await getPlatforms();
            setPlatforms(data || []);
        } catch (error) {
            console.error('Error loading platforms:', error);
            toast.error('Failed to load SDK platforms');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPlatforms();
    }, []);

    const handleDragEnd = async ({ active, over }) => {
        if (over && active.id !== over.id) {
            const oldIndex = platforms.findIndex(p => p.id === active.id);
            const newIndex = platforms.findIndex(p => p.id === over.id);
            const reordered = arrayMove(platforms, oldIndex, newIndex);
            setPlatforms(reordered);
            try {
                await reorderPlatforms(reordered.map((p, i) => ({ id: p.id, order: i + 1 })));
            } catch (error) {
                console.error('Error reordering platforms:', error);
                toast.error('Failed to save order');
            }
        }
    };

    const handleToggle = async (platform) => {
        const updated = !platform.enabled;
        setPlatforms(prev => prev.map(p => p.id === platform.id ? { ...p, enabled: updated } : p));
        try {
            await updatePlatform(platform.id, { enabled: updated });
            toast.success(`${platform.label} ${updated ? 'enabled' : 'disabled'}`);
        } catch (error) {
            console.error('Error toggling platform:', error);
            setPlatforms(prev => prev.map(p => p.id === platform.id ? { ...p, enabled: !updated } : p));
            toast.error('Failed to update platform');
        }
    };

    const saveOrder = async () => {
        try {
            await reorderPlatforms(platforms.map((p, i) => ({ id: p.id, order: i + 1 })));
            toast.success('Order saved');
        } catch (error) {
            console.error('Error saving order:', error);
            toast.error('Failed to save order');
        }
    };

    const handleAddPlatform = async (data) => {
        const isDuplicate = platforms.some(
            p => p.label.trim().toLowerCase() === data.label.trim().toLowerCase()
                && Number(p.type_id) === Number(data.type_id)
        );
        if (isDuplicate) {
            toast.error(`A ${getDocType(data.type_id).label} platform named "${data.label}" already exists`);
            return;
        }
        setAdding(true);
        try {
            await createPlatform(data);
            await loadPlatforms();
            toast.success(`${data.label} added`);
            setIsAddOpen(false);
        } catch (error) {
            console.error('Error adding platform:', error);
            toast.error('Failed to add platform');
        } finally {
            setAdding(false);
        }
    };

    const handleDeletePlatform = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deletePlatform(deleteTarget.id);
            setPlatforms(prev => prev.filter(p => p.id !== deleteTarget.id));
            toast.success(`${deleteTarget.label} deleted`);
            setDeleteTarget(null);
        } catch (error) {
            console.error('Error deleting platform:', error);
            toast.error('Failed to delete platform');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Header />
            <BoxWrapper>
                <Flex justify="space-between" align="flex-start" mb={6}>
                    <Box>
                        <Text fontSize="xl" fontWeight="bold" color="gray.700">SDK Configuration</Text>
                        <Text fontSize="sm" color="gray.500" mt={1}>
                            Manage SDK integration guides. Drag to reorder, toggle visibility, and edit documentation.
                        </Text>
                    </Box>
                    <HStack spacing={2}>
                        <Button
                            size="sm"
                            leftIcon={<Icon as={LuPlus} />}
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => setIsAddOpen(true)}
                        >
                            Add Platform
                        </Button>
                        <Button size="sm" colorScheme="blue" onClick={saveOrder}>
                            Save Order
                        </Button>
                    </HStack>
                </Flex>

                {loading ? (
                    <Center h="200px">
                        <Spinner size="lg" color="blue.500" thickness="3px" />
                    </Center>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={platforms.map(p => p.id)} strategy={verticalListSortingStrategy}>
                            <VStack spacing={2} align="stretch">
                                {platforms.map(platform => (
                                    <SortableRow
                                        key={platform.id}
                                        platform={platform}
                                        onToggle={handleToggle}
                                        onEdit={(id) => navigate(`/sdk-config/${id}`)}
                                        onDelete={setDeleteTarget}
                                    />
                                ))}
                            </VStack>
                        </SortableContext>
                    </DndContext>
                )}
            </BoxWrapper>

            <AddPlatformModal
                isOpen={isAddOpen}
                onClose={() => setIsAddOpen(false)}
                onSave={handleAddPlatform}
                saving={adding}
            />

            <AlertDialog
                isOpen={!!deleteTarget}
                leastDestructiveRef={cancelDeleteRef}
                onClose={() => setDeleteTarget(null)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Platform
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete "{deleteTarget?.label}"? This will remove its documentation and cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelDeleteRef} onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleDeletePlatform}
                                isLoading={deleting}
                                ml={3}
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default SdkConfigPage;
